import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { USD_NEMO } from '@constants/miscellaneous';
import { ICompleteUser } from '@interfaces/investment-form';
import { IUserInformationConstants } from '@interfaces/user-information-constants';
import { IUser, IUserSocialProfile, IUserSocialProfileInvestments, IActivity } from '@interfaces/users';
import { IMinimumUser } from '@interfaces/users';
import { FileService } from '@services/file/file.service';
import { PortfolioService } from '@services/portfolio/portfolio.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  userId: string;
  userSub: any;
  oneTimeOnlyCreateProfile: boolean;
  loadedProfiles: { [key: string]: IUserSocialProfile };
  socialProfileSub: any;
  private socialProfileSource = new BehaviorSubject<IUserSocialProfile>(null);
  currentSocialProfileSource = this.socialProfileSource.asObservable();

  constructor(
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
    private fileService: FileService,
    private portfolioService: PortfolioService,
  ) {
    this.loadedProfiles = {};
  }

  getFullUserObs(uid: string, subject: BehaviorSubject<IUser>, emailVerified: boolean) {
    this.userId = uid;
    this.userSub = this.firebaseService.getDocumentObs('users', uid, subject, { emailVerified });
  }

  getMinUser(id: string) {
    const collection = this.firebaseService.getCollection('users');
    return this.firebaseService.getDocumentObject(id, collection)
    .then((user) => {
      return user.data() as IMinimumUser;
    })
    .catch((err) => {
      this.firebaseService.logError(err.message);
      console.log(err);
    });
  }

  getFullUser(id: string) {
    this.userId = id;
    const collection = this.firebaseService.getCollection('users');
    return this.firebaseService.getDocumentObject(id, collection)
    .then((user) => {
      return user.data() as IUser;
    })
    .catch((err) => {
      this.firebaseService.logError(err.message);
      console.log(err);
    });
  }

  async getAllUserInformationConstants() {
    try {
      const collection = this.firebaseService.getCollection('userInformationConstants');
      const snapshot =  await this.firebaseService.getAllDocuments(collection);
      const userInformationConstants: IUserInformationConstants = {
        banks: null,
        counties: null,
        maritalStatus: null,
        workerTypes: null,
        employmentTypes: null,
        workerPositions: null,
        nationalities: null,
        regions: null,
        USPerson: null,
        PEP: null,
        matrimonialRegime: null,
        stockBrokerInformation: null,
      };
      snapshot.docs.map(doc => {
        userInformationConstants[doc.id] = doc.data();
      });
      return userInformationConstants;
    } catch (err) {
      throw err;
    }
  }

  setTemporaryInfoComplete(uid) {
    return this.firebaseService.updateDocumentWithMerge({ temporaryInfoComplete: true }, this.firebaseService.getCollection('users'), uid);
  }

  async updateUser(user: ICompleteUser) {
    try {
      const userObject = {...user};
      const uid = userObject.userId;
      delete userObject.userId;
      const collection = this.firebaseService.getCollection('users');
      return await this.firebaseService.updateDocumentWithMerge(userObject, collection, uid);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al actualizar los datos, inténtalo nuevamente', false);
      throw err;
    }
  }

  async createEmptySocialProfile(userInfo: IUser) {
    if (this.oneTimeOnlyCreateProfile) {
      return;
    }
    this.oneTimeOnlyCreateProfile = true;
    let username = this.usernameFromNickname(userInfo.personalInfo.nickName);
    let usernameExists = await this.checkUsernameExists(username);
    if (usernameExists) {
      const originalUsername = username;
      while (usernameExists) {
        username = originalUsername + Math.floor(Math.random() * 10000);
        usernameExists = await this.checkUsernameExists(username);
      }
    }
    const profilesRef = this.firebaseService.getCollection('profiles');
    const newProfileRefId = profilesRef.doc().id;
    const newSocialProfile = {
      profileId: newProfileRefId,
      displayName: userInfo.personalInfo.names.split(' ')[0].concat(' ', userInfo.personalInfo.lastNames.split(' ')[0]),
      username,
      animalImage: userInfo.uid.split('').map(s => s.charCodeAt(0)).reduce((a, b) => a + b, 0) % 12,
      hasUser: true,
    };
    const usersRef = this.firebaseService.getCollection('users');
    await this.firebaseService.updateDocument({ profileId: newProfileRefId }, usersRef, userInfo.uid);
    this.getOwnSocialProfileObs(newProfileRefId);
    await this.firebaseService.addDocumentWithID(newSocialProfile, profilesRef, newProfileRefId);
  }

  async setProfileInformation(profile: IUserSocialProfile, profileId) {
    const profilesRef = this.firebaseService.getCollection('profiles');
    return this.firebaseService.updateDocument(profile, profilesRef, profileId);
  }

  async getSocialProfileFromCache(profileId: string) {
    if (!this.loadedProfiles[profileId]) {
      this.loadedProfiles[profileId] = await this.getSocialProfileInformation(profileId);
    }
    return this.loadedProfiles[profileId];
  }

  async getSocialProfileInformation(profileId: string) {
    if (!profileId) {
      return null;
    }
    const profilesRef = this.firebaseService.getCollection('profiles');
    const profile = await this.firebaseService.getDocument(profileId, profilesRef) as IUserSocialProfile;
    return profile ? { ...profile, profileId } : (await this.getSocialProfileByUsername(profileId));
  }

  getSocialProfileByUsername(username: string) {
    return this.firebaseService.firestore.collection('profiles').where('username', '==', username).limit(1).get()
    .then(profilesRef => {
      return profilesRef.size ? { ...profilesRef.docs[0].data(), profileId: profilesRef.docs[0].id } as IUserSocialProfile : null;
    });
  }

  getOwnSocialProfileObs(profileId: string) {
    this.socialProfileSub = profileId ? this.firebaseService.getDocumentObs('profiles', profileId, this.socialProfileSource) : () => {};
  }

  async getSocialProfileInvestments(profileId: string) {
    const profileInvestmentsRef = this.firebaseService.getCollection(`profiles/${profileId}/financialData`);
    return await this.firebaseService.getDocument('investments', profileInvestmentsRef) as IUserSocialProfileInvestments;
  }

  async setExtendedInformation(user: ICompleteUser) {
    const userObject = {...user};
    const uid = userObject.userId;
    const collection = this.firebaseService.getCollection('extendedUserInformation');
    return await this.firebaseService.addDocumentWithID(userObject, collection, uid);
  }

  async listenToIdentificationDocument(userId: string, fileName: string, subject: BehaviorSubject<any>, frontSide: boolean) {
    let doc: any;
    if (frontSide) {
      const collection = this.firebaseService.getCollection('frontIDRecognitions');
      doc = await this.firebaseService.addDocument({
        userId,
        fileName,
      }, collection);
    } else {
      doc = await this.firebaseService.getCollection('frontIDRecognitions')
      .where('userId', '==', userId).orderBy('createdAt', 'desc').limit(1).get()
      .then((frontIDRecognitionsRef) => {
        return frontIDRecognitionsRef.docs[0];
      });
    }
    return this.firebaseService.getDocumentObs('frontIDRecognitions', doc.id, subject);
  }

  async resendVerificationEmail(uid: string) {
    const collection = this.firebaseService.getCollection('resendVerificationEmails');
    return await this.firebaseService.addDocument({ uid }, collection);
  }

  createContractUpdate(contractUpdate) {
    return new Observable(observer => {
      Promise.all([
        this.firebaseService.addDocumentWithID(contractUpdate, this.firebaseService.getCollection('contractUpdates'), contractUpdate.id),
      ])
        .then(() => {
            const subscription = this.firebaseService.firestore.doc(`/contractUpdates/${contractUpdate.id}`).onSnapshot((doc) => {
              let returnValue = null;
              if (doc.data().status === 'complete') {
                returnValue = true;
                observer.next(returnValue);
                subscription();
              }
            },
            () => {
              observer.next(false);
              subscription();
            });
          })
        .catch(err => {
          this.firebaseService.logError(err.message);
          observer.next(false);
        });
    });
  }

  async callUpdateUser() {
    return this.firebaseService.callableFunction('UserUpdateDateCallableGen2');
  }

  updateLastUserReadDate(date: Date) {
    const usersRef = this.firebaseService.getCollection('users');
    const userNewInfo = { lastReadDate: date };
    this.firebaseService.updateDocument(userNewInfo, usersRef, this.userId);
  }

  async updateUserField(fieldModified: string, newValue: any, oldValue: any) {
    const usersRef = this.firebaseService.getCollection('users');
    const userNewInfo = {
      [fieldModified]: newValue,
    };
    this.firebaseService.updateDocument(userNewInfo, usersRef, this.userId)
    .then(() => {
      const updatesCollection = this.firebaseService.getCollection(`users/${this.userId}/historicUpdates`);
      const updateDocument = {
        attribute: fieldModified,
        newValue: newValue,
        oldValue,
      };
      const nowString = (new Date()).toLocaleString('es-CL')
        .replace(',', '').split(' ').map(t => t.split('-').reverse().join('-')).join('_');
      this.firebaseService.addDocumentWithID(updateDocument, updatesCollection, `${nowString}_${updateDocument.attribute}`);
    });
  }

  async checkUsernameExists(username: string) {
    const profilesRef = await this.firebaseService.getCollection('profiles').where('username', '==', username).limit(1).get();
    return !profilesRef.empty;
  }

  usernameFromNickname(nickname: string): string {
    let cleaned = nickname.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ /g, '');
    if (!cleaned.match(/^[a-z].*$/i)) {
      cleaned = 'el_' + cleaned;
    }
    if (!cleaned.match(/(.*[a-z]){3}/)) {
      cleaned = cleaned + '_usr';
    }
    return cleaned.slice(0, 15);
  }

  validateHasLetters() {
    return (control: AbstractControl) => {
      if ((control.value as string).match(/(.*[a-z]){3}/i)) {
        return null;
      }
      return { fewLetters: true };
    };
  }

  validateStartsWithALetter() {
    return (control: AbstractControl) => {
      if ((control.value as string).trim().match(/^[a-z].*$/i)) {
        return null;
      }
      return { noStartLetter: true };
    };
  }

  validateUsernameNotUsed(oldUsername) {
    return async (control: AbstractControl) => {
      if (control.value === oldUsername) {
        return null;
      }
      const res = await this.checkUsernameExists(control.value);
      return res ? { usernameExists: true } : null;
    };
  }

  followUserFeed(followerProfileId: string, followedProfileId: string) {
    const followRequestsRef = this.firebaseService.getCollection(`profiles/${followerProfileId}/followRequests`);
    const docId = followerProfileId.concat('_', followedProfileId);
    const data = { followerProfileId, followedProfileId };
    this.firebaseService.addDocumentWithID(data, followRequestsRef, docId);
  }

  unfollowUserFeed(followerProfileId: string, followedProfileId: string) {
    const followRequestsRef = this.firebaseService.getCollection(`profiles/${followerProfileId}/followRequests`);
    const docId = followerProfileId.concat('_', followedProfileId);
    return this.firebaseService.updateDocument({ active: false }, followRequestsRef, docId);
  }

  getFollowRequest(followerProfileId: string, followedProfileId: string) {
    const docId = followerProfileId.concat('_', followedProfileId);
    const followRequestsRef = this.firebaseService.getCollection('followRequests');
    return this.firebaseService.getDocument(docId, followRequestsRef);
  }

  async getUserProfileStats(profileId: string) {
    const getUserProfileStatsFn = this.firebaseService.functions.httpsCallable('GetUserProfileStats');
    return (await getUserProfileStatsFn({ profileId })).data;
  }

  async getGroupOfProfileActivities(profileId: string, lastDocRef?) {
    let query = this.firebaseService.getCollection(`profiles/${profileId}/activities`).orderBy('date', 'desc');
    query = lastDocRef ? query.startAfter(lastDocRef) : query;
    return query.limit(10).get().then(activitiesRef => {
      const activities = activitiesRef.docs.map(doc => ({ ...doc.data(), date: doc.data().date.toDate() } as IActivity));
      return { activities, lastDocRef: activitiesRef.docs[activitiesRef.docs.length - 1] };
    });
  }

  async uploadProfilePicture(profileId, showDeleteOption) {
    const uploaded = await this.fileService.showUploadOptions(0, true, showDeleteOption);
    if (uploaded ===  'delete') {
      await this.firebaseService.firestore.doc(`/profiles/${profileId}`).update({ profileImage: null, profileImageUpdatedAt: new Date() });
      return true;
    } else if (uploaded) {
      const task = (await this.fileService.uploadAllFiles(`Users/${this.userId}/Profile`, ['profileImage.jpeg']))[0];
      const self = this;
      task.on('state_changed', function (snapshot) {
      }, function (err) {
        self.firebaseService.logError(err.message);
        return false;
      });
      return true;
    }
    return false;
  }

  async updateRecurrentBuysProfile(profileId, recurrentBuys) {
    return this.firebaseService.firestore.doc(`/profiles/${profileId}/financialData/investments`).update({ recurrentBuys });
  }

  userHasAsset(user: IUser, assetId: string) {

    if (user && user.lastHistoricEvolution && user.lastHistoricEvolution.effectivePortfolioComposition) {
      return user.lastHistoricEvolution.effectivePortfolioComposition.some(asset => asset.assetId === assetId);
    }

    return false;
  }

  async getDwPortfolioId() {
    const portfolios = await this.portfolioService.getPortfolios(this.userId);
    return portfolios.find(portfolio => portfolio.isDrivewealth).id;
  }
}
